/**
 * CasualCompetitors 页面接口服务
 * @description 各模块单独封装接口方法，通过 type 参数区分调用来源
 */
import { fetchStudioCasualCompetitorsMetrics } from '@home/services/studio';
import { getGameInfoByIdPlus } from '@intelligence/services/common';
import { appendAdjustedRevenueFilter } from '@intelligence/utils/revenueScope';
import { isEmpty } from '@intelligence/v3/utils/common';
import {
  DEFAULT_SENSORTOWER_DATA_SOURCE,
  MAX_PAGE_SIZE,
  getDataSourceCustomFieldKey,
  getMetricOrderBy,
} from '../constants';

import type { AxiosRequestConfig } from 'axios';
import type { IStudioCasualCompetitorsMetricsFetchParams } from '@home/types/studio';

const DEFAULT_GAME_DETAILS_CUSTOM_FIELDS = [
  'entity_name',
  'cover',
  'publisher',
  'country_abbre',
  getDataSourceCustomFieldKey(DEFAULT_SENSORTOWER_DATA_SOURCE, 'sub_genre'),
];

const DEFAULT_TOP_PUBLISHERS_ORDER_BY = [getMetricOrderBy(DEFAULT_SENSORTOWER_DATA_SOURCE, 'daily_avg_dau')];

const MERGE_2_GENRE_IDS = ['merge_2', 'merge_2_narrative'];

const isMerge2Genre = (genreId?: string): boolean =>
  !!genreId && MERGE_2_GENRE_IDS.includes(genreId);

const appendRevenueScopeParams = <T extends IRecord>(data: T): T => {
  const { revenue_scope: revenueScope, ...restData } = data || {};

  if (isEmpty(revenueScope)) {
    return restData as unknown as T;
  }

  return appendAdjustedRevenueFilter(restData, revenueScope as string) as T;
};

/**
 * 品类大盘 - KPI指标数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchCategoryOverviewKpi = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'order_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'category_overview_kpi',
    appendRevenueScopeParams({
      ...data,
      group_by: [],
      order_by: [],
      page: 1,
      page_size: 1,
      filter: {
        ...data?.filter,
        enable_game_id_filter: data?.filter?.enable_game_id_filter ?? false,
        enable_genre_filter: true,
      },
    }),
    config,
  );
};

/**
 * 品类大盘 - 折线图数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchCategoryOverviewChart = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'order_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'category_overview_chart',
    appendRevenueScopeParams({
      ...data,
      group_by: ['date'],
      order_by: [],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      filter: {
        ...data?.filter,
        enable_game_id_filter: data?.filter?.enable_game_id_filter ?? false,
        enable_genre_filter: true,
      },
    }),
    config,
  );
};

interface IKeyGamesEnableConfig {
  enable_game_id_filter: boolean;
  enable_genre_filter: boolean;
  enable_darkhorse_filter: boolean;
  enable_dau_share_by_all_games: boolean;
}

export const keyGamesEnableConfigs: Record<string, IKeyGamesEnableConfig> = {
  top_games: {
    enable_game_id_filter: true,
    enable_genre_filter: false,
    enable_darkhorse_filter: false,
    enable_dau_share_by_all_games: true,
  },
  rising_games: {
    enable_game_id_filter: false,
    enable_genre_filter: true,
    enable_darkhorse_filter: true,
    enable_dau_share_by_all_games: true,
  },
};

/**
 * 热门游戏 - 表格数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchTopGamesTable = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  const merge2Filter = isMerge2Genre(data?.filter?.genre_id)
    ? {
      enable_game_id_filter: false,
      enable_game_filter: true,
      enable_genre_filter: true,
    }
    : {};

  return fetchStudioCasualCompetitorsMetrics(
    'top_games_table',
    appendRevenueScopeParams({
      ...data,
      group_by: ['game_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_custom_fields: data?.use_custom_fields || DEFAULT_GAME_DETAILS_CUSTOM_FIELDS,
      filter: {
        ...data?.filter,
        ...keyGamesEnableConfigs.top_games,
        ...merge2Filter,
      },
    }),
    config,
  );
};

/**
 * 热门游戏 - 折线图数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchTopGamesChart = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'top_games_chart',
    appendRevenueScopeParams({
      ...data,
      group_by: ['date', 'game_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_custom_fields: ['entity_name'],
      filter: {
        ...data?.filter,
        ...keyGamesEnableConfigs.top_games,
        enable_game_id_filter: false,
      },
    }),
    config,
  );
};

/**
 * 黑马游戏 - 表格数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchRisingGamesTable = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'rising_games_table',
    appendRevenueScopeParams({
      ...data,
      group_by: ['game_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_custom_fields: data?.use_custom_fields || DEFAULT_GAME_DETAILS_CUSTOM_FIELDS,
      filter: {
        ...data?.filter,
        ...keyGamesEnableConfigs.rising_games,
      },
    }),
    config,
  );
};

/**
 * 黑马游戏 - 折线图数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchRisingGamesChart = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'rising_games_chart',
    appendRevenueScopeParams({
      ...data,
      group_by: ['date', 'game_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_custom_fields: ['entity_name'],
      filter: {
        ...data?.filter,
        ...keyGamesEnableConfigs.rising_games,
        enable_game_id_filter: false,
        enable_darkhorse_filter: false,
      },
    }),
    config,
  );
};

/**
 * 头部厂商 - 数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchTopPublishers = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  const isMerge2 = isMerge2Genre(data?.filter?.genre_id);

  return fetchStudioCasualCompetitorsMetrics(
    'top_publishers',
    appendRevenueScopeParams({
      ...data,
      group_by: ['company_id'],
      order_by: data?.order_by || DEFAULT_TOP_PUBLISHERS_ORDER_BY,
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_entity_type: 'company',
      use_custom_fields: ['name', 'image_url', 'country_abbre', 'country_name_en'],
      filter: {
        ...data?.filter,
        enable_game_id_filter: false,
        enable_genre_filter: true,
        enable_company_id_filter: !isMerge2,
      },
    }),
    config,
  );
};

/**
 * 头部厂商 - 厂商下的游戏数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchTopPublishersGames = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  return fetchStudioCasualCompetitorsMetrics(
    'top_publishers_games',
    appendRevenueScopeParams({
      ...data,
      group_by: ['company_id', 'game_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      filter: {
        ...data?.filter,
        enable_game_id_filter: false,
        enable_genre_filter: true,
        enable_dau_share_by_company_games: true,
      },
    }),
    config,
  );
};

/**
 * 头部厂商 - 折线图数据
 * @param data 接口入参
 * @param config axios请求配置
 */
export const fetchTopPublishersChart = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => {
  const isMerge2 = isMerge2Genre(data?.filter?.genre_id);

  return fetchStudioCasualCompetitorsMetrics(
    'top_publishers_chart',
    appendRevenueScopeParams({
      ...data,
      group_by: ['date', 'company_id'],
      page: 1,
      page_size: MAX_PAGE_SIZE,
      use_entity_type: 'company',
      use_custom_fields: ['name', 'image_url', 'country_abbre', 'country_name_en'],
      filter: {
        ...data?.filter,
        enable_game_id_filter: false,
        enable_genre_filter: true,
        enable_company_id_filter: !isMerge2,
      },
    }),
    config,
  );
};

/**
 * 获取游戏详情数据
 * @description 通过 detail_plus 接口单独获取游戏详情
 * @param gameIds 游戏ID列表
 * @param customFields ext扩展字段
 * @param config axios请求配置
 */
export const fetchGamesDetails = async (
  gameIds: string[],
  customFields = DEFAULT_GAME_DETAILS_CUSTOM_FIELDS,
  config?: AxiosRequestConfig,
): Promise<IRecord> => {
  if (isEmpty(gameIds)) {
    return {};
  }

  const res = await getGameInfoByIdPlus(
    {
      entity_type: 'mobile',
      level: 'custom',
      custom_fields: customFields,
      ids: gameIds,
    },
    config,
  );

  return res?.data || {};
};
